<template>
  <div class="space-y-6 max-w-4xl">
    <Head title="Nueva Tasa de Cambio" />

    <!-- Migas de pan / Cabecera -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <Link class="text-orange-600 hover:text-orange-700 font-semibold" href="/taxes">Tasas de cambio</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-700 font-medium">Nueva tasa</span>
      </div>
      <Link href="/taxes" class="text-xs text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1.5">
        <i class="fas fa-arrow-left text-[10px]" /> Volver al listado
      </Link>
    </div>

    <!-- Banner sugerencia BCV -->
    <div v-if="bcvData?.rate" class="p-4 bg-orange-50/60 border border-orange-200/60 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
      <div class="flex items-center gap-3">
        <div class="w-8 h-8 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center text-xs">
          <i class="fas fa-university" />
        </div>
        <div>
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-gray-800">Tasa Oficial BCV: Bs. {{ bcvData.formatted_rate || formatPrice(bcvData.rate) }}</span>
            <span class="text-[10px] text-gray-400">({{ bcvData.date || 'Actual' }})</span>
          </div>
          <p class="text-[11px] text-gray-500">Puedes cargar este valor automáticamente o ingresar una tasa personalizada.</p>
        </div>
      </div>

      <button 
        type="button" 
        class="h-8 px-3.5 bg-white hover:bg-orange-50 text-orange-600 border border-orange-200/80 rounded-xl text-xs font-semibold shadow-2xs transition-all active:scale-95 flex items-center justify-center gap-1.5 self-start sm:self-auto" 
        @click="useBcvValue"
      >
        <i class="fas fa-magic text-[10px]" /> Usar valor del BCV
      </button>
    </div>

    <!-- Formulario principal -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-2xs overflow-hidden">
      <div class="p-6 border-b border-gray-100">
        <h2 class="text-sm font-bold text-gray-900">Registrar Tasa de Cambio</h2>
        <p class="text-xs text-gray-500 mt-0.5">Define el valor de conversión en bolívares para las operaciones del sistema.</p>
      </div>

      <form @submit.prevent="store">
        <div class="p-6 space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Campo Tasa USD / Bs -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Tasa USD (Bolívares) *</label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">Bs.</span>
                <input 
                  v-model.number="form.change" 
                  type="number" 
                  step="0.0001"
                  min="0.0001"
                  required 
                  placeholder="Ej: 880.50"
                  class="w-full text-xs rounded-xl border-gray-200 pl-9 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-bold text-gray-900 transition-colors"
                  :class="{ 'border-red-300': form.errors.change }"
                />
              </div>
              <p v-if="form.errors.change" class="text-[10px] text-red-500 mt-1">{{ form.errors.change }}</p>
            </div>

            <!-- Campo Tasa COP -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Tasa Pesos Colombianos (COP) <span class="font-normal text-gray-400">(opcional)</span></label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">COP</span>
                <input 
                  v-model.number="form.cop_change" 
                  type="number" 
                  step="0.01"
                  min="0" 
                  placeholder="Ej: 4150.00"
                  class="w-full text-xs rounded-xl border-gray-200 pl-11 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold text-gray-900 transition-colors"
                />
              </div>
            </div>
          </div>

          <!-- Selector de Cuenta si aplica -->
          <div v-if="accounts && accounts.length > 1">
            <label class="block text-xs font-semibold text-gray-700 mb-1.5">Ámbito / Cuenta asignada</label>
            <select 
              v-model="form.account_id" 
              class="w-full text-xs rounded-xl border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 py-2 px-3 transition-colors"
            >
              <option value="">Global (aplica para todas las sedes)</option>
              <option v-for="account in accounts" :key="account.id" :value="account.id">{{ account.name || account.reasonName }}</option>
            </select>
          </div>
        </div>

        <div class="px-6 py-4 bg-gray-50/70 border-t border-gray-100 flex items-center justify-end gap-3">
          <Link href="/taxes" class="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors">
            Cancelar
          </Link>
          <button 
            type="submit" 
            :disabled="form.processing || !form.change"
            class="h-9 px-5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-xl shadow-xs active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-50"
          >
            <i v-if="form.processing" class="fas fa-spinner fa-spin text-xs" />
            <i v-else class="fas fa-check text-xs" />
            <span>{{ form.processing ? 'Guardando...' : 'Crear Tasa de Cambio' }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,

  props: {
    accounts: Array,
    bcvData: Object,
  },

  data() {
    return {
      form: this.$inertia.form({
        change: this.bcvData?.rate ? Number(this.bcvData.rate) : '',
        cop_change: '',
        account_id: this.accounts && this.accounts.length === 1 ? this.accounts[0].id : '',
      }),
    }
  },

  methods: {
    formatPrice(val) {
      if (val === null || val === undefined || isNaN(val)) return '0.00'
      return Number(val).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    },

    useBcvValue() {
      if (this.bcvData?.rate) {
        this.form.change = Number(this.bcvData.rate)
      }
    },

    store() {
      this.form.post('/taxes')
    },
  },
}
</script>